import React, { useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import { ArrowBack, CardGiftcard } from "@mui/icons-material";
import { Scaffold } from "../Scaffold";
import LoadingSpinner from "../LoadingSpinner";
import { State } from "../redux/types";
import { useSelector, useDispatch } from "react-redux";
import { ConfirmActionDialog } from "../components/ConfirmActionDialog";
import { VendorUpdateDialog } from "../components/VendorUpdateDialog";
import { useVendorActions } from "../hooks/useVendorActions";
import { vendorRequest } from "../api/api";
import { setAdvanceResult } from "../redux/actions";
import { maskPhone } from "../utils/masking";

const VENDOR_KEY = "punchh";

export const Punchh = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const advanceVendorResult = useSelector((state: State) => state.advanceResult);
  
  // Get customerVendorId from navigation state (passed from AdvanceResult)
  const locationState = location.state as { customerVendorId?: string } | null;

  const [offersModalOpen, setOffersModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Try multiple possible property names for the Punchh ID, with navigation state as fallback
  const punchhId = advanceVendorResult?.customer?.punchhId 
    ?? advanceVendorResult?.customer?.customerVendorId 
    ?? advanceVendorResult?.customer?.id
    ?? advanceVendorResult?.value?.punchhId
    ?? advanceVendorResult?.value?.customerVendorId
    ?? advanceVendorResult?.value?.id
    ?? locationState?.customerVendorId
    ?? "";

  // Get rewards offers array
  const rewardOffers: string[] = advanceVendorResult?.customer?.rewardOffersList ?? [];
  const hasMultipleOffers = Array.isArray(rewardOffers) && rewardOffers.length > 1;

  // Get display text for rewards offers field
  const getOffersDisplayText = (): string => {
    if (!rewardOffers || rewardOffers.length === 0) {
      return "No Reward Offers";
    }
    if (rewardOffers.length === 1) {
      return rewardOffers[0];
    }
    return `${rewardOffers.length} reward offers`;
  };

  const handleBackToResults = () => {
    navigate("/advanceresult");
  };

  // Refresh vendor data after successful action
  const handleVendorActionSuccess = useCallback(async (actionKey: string) => {
    if (actionKey === "delete") {
      navigate("/advanceresult");
      return;
    }

    if (!punchhId) { return; }

    setIsRefreshing(true);
    try {
      await new Promise<void>(resolve => { setTimeout(resolve, 1000); });
      const response = await vendorRequest(punchhId, VENDOR_KEY);
      if (response) {
        dispatch(setAdvanceResult({ ...response }));
      }
    } catch (error) {
      console.debug("Punchh vendor refetch failed", error);
    } finally {
      setIsRefreshing(false);
    }
  }, [punchhId, dispatch, navigate]);

  // ============================================
  // Vendor Actions (UPDATE & DELETE)
  // ============================================

  const {
    dialogOpen: vendorDialogOpen,
    updateDialogOpen: vendorUpdateDialogOpen,
    currentAction: vendorCurrentAction,
    loading: vendorLoading,
    openDialog: openVendorDialog,
    closeDialog: closeVendorDialog,
    closeUpdateDialog: closeVendorUpdateDialog,
    confirmAction: confirmVendorAction,
    confirmUpdate: confirmVendorUpdate,
  } = useVendorActions({
    vendorKey: VENDOR_KEY,
    customerVendorId: punchhId,
    vendorName: "Punchh",
    onSuccess: handleVendorActionSuccess,
    onFailure: () => {
      // Handle failure if needed
    },
  });

  if (!advanceVendorResult) {
    return (
      <Scaffold navBar center>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
          <LoadingSpinner />
        </Box>
      </Scaffold>
    );
  }

  return (
    <Scaffold navBar center>
      {isRefreshing && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            bgcolor: "rgba(255,255,255,0.7)",
          }}
        >
          <LoadingSpinner />
        </Box>
      )}
      {/* Back Button - Fixed to top left */}
      <Box
        sx={{
          position: "fixed",
          top: 80,
          left: 20,
          zIndex: 10,
          mt: "2rem",
        }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBackToResults}
          sx={{
            color: "text.secondary",
            textTransform: "none",
            fontSize: "0.875rem",
          }}
        >
          BACK TO RESULTS
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        {/* Punchh Title - Centered */}
        <Typography
          variant="h4"
          sx={{ mb: 3, fontWeight: "bold", textAlign: "center", width: "100%" }}
        >
          Punchh
        </Typography>

        {/* Customer Account Card */}
        <Card 
            sx={{ 
              border: "3px solid rgba(0,0,0,0.1)", 
              borderRadius: 2,
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              minWidth: "400px",
              maxWidth: "600px"
            }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 3, 
                fontWeight: "bold",
                color: "#333"
              }}
            >
              Customer Account
            </Typography>

            {advanceVendorResult ? (
                  <Stack spacing={1.5}>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>Name:</Typography>
                      <Typography variant="body2" sx={{ textAlign: "right" }}>
                        {advanceVendorResult.customer.firstName} {advanceVendorResult.lastName}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>Email:</Typography>
                      <Typography variant="body2" sx={{ textAlign: "right" }}>
                        {advanceVendorResult.customer.email}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>Phone:</Typography>
                      <Typography variant="body2" sx={{ textAlign: "right" }}>
                        {maskPhone(advanceVendorResult.customer.mobilePhoneNumber ?? advanceVendorResult.customer.phoneNumber ?? "")}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>Rewards Offers:</Typography>
                      {hasMultipleOffers ? (
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => setOffersModalOpen(true)}
                          sx={{
                            textTransform: "none",
                            color: "#041E42",
                            fontWeight: "bold",
                            textDecoration: "underline",
                            p: 0,
                            minWidth: "auto",
                            "&:hover": {
                              backgroundColor: "transparent",
                              textDecoration: "underline",
                            },
                          }}
                        >
                          {getOffersDisplayText()}
                        </Button>
                      ) : (
                        <Typography variant="body2" sx={{ textAlign: "right" }}>
                          {getOffersDisplayText()}
                        </Typography>
                      )}
                    </Box>
                    
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>Reward Points:</Typography>
                      <Typography variant="body2" sx={{ textAlign: "right" }}>
                        {advanceVendorResult.customer?.redeemablePoints}
                      </Typography>
                    </Box>
                    
                    
                  </Stack>
            ) : (
              <Typography variant="body1" color="text.secondary">
                No customer data available
              </Typography>
            )}

            {/* Action Buttons */}
            <Box sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "center" }}>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#041E42",
                  color: "white",
                  px: 4,
                  py: 1,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#0a3d7a"
                  }
                }}
                onClick={() => openVendorDialog("update")}
              >
                UPDATE
              </Button>
              
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#b71c1c",
                  color: "white",
                  px: 4,
                  py: 1,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#7f0000"
                  }
                }}
                onClick={() => openVendorDialog("delete")}
              >
                DELETE
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Rewards Offers Modal */}
      <Dialog
        open={offersModalOpen}
        onClose={() => setOffersModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CardGiftcard color="primary" />
          Reward Offers ({rewardOffers.length})
        </DialogTitle>
        <DialogContent dividers>
          <List disablePadding>
            {rewardOffers.map((offer, offerIdx) => (
              <React.Fragment key={typeof offer === "string" ? offer : JSON.stringify(offer)}>
                {offerIdx > 0 && <Divider />}
                <ListItem sx={{ py: 2 }}>
                  <ListItemText
                    primary={
                      <Typography variant="body2">
                        {offer}
                      </Typography>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOffersModalOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Vendor Actions (DELETE) */}
      <ConfirmActionDialog
        open={vendorDialogOpen}
        actionLabel={vendorCurrentAction?.label ?? ""}
        description={vendorCurrentAction?.description ?? ""}
        confirmText={vendorCurrentAction?.confirmText}
        loading={vendorLoading}
        onConfirm={confirmVendorAction}
        onCancel={closeVendorDialog}
      />

      {/* Vendor Update Dialog with Form */}
      <VendorUpdateDialog
        open={vendorUpdateDialogOpen}
        vendorName="Punchh"
        loading={vendorLoading}
        initialValues={{
          firstName: advanceVendorResult?.customer?.firstName,
          lastName: advanceVendorResult?.customer?.lastName,
          email: advanceVendorResult?.customer?.email,
          phoneNumber: advanceVendorResult?.customer?.mobilePhoneNumber,
          dateOfBirth: advanceVendorResult?.customer?.birthDate,
        }}
        onConfirm={confirmVendorUpdate}
        onCancel={closeVendorUpdateDialog}
        hideBirthDate
      />
    </Scaffold>
  );
};

